import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { forkJoin, from, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { environment as ENV } from '../../../environments/environment';
import { URL_MAP } from '../../shared/urlMap';

/**
 * ## Flujo post-radicación (MP-FT-1358 — ver `DOCUMENTOS-GENERADOS-API-FRONTEND.md` en backend)
 *
 * **Obligatorio para firma:** `POST …/reports/generar-documentos-solicitud` **una vez por integrante activo**
 * (Jasper + file server + `RUTA_ARCHIVO` en `TRL_DOCUMENTOS_GENERADOS_SOLICITUD`). Después:
 * `GET …/documentos-generados/validar-firma`.
 *
 * **No completa firma:** `POST …/reports/{nombreClaveJasper}` (solo bytes PDF, sin ruta en BD).
 *
 * **Opcional / progreso:** `POST …/documentos-parametrizados` (cuántos formatos aplican).
 * **Opcional / resumen:** `GET …/documentos-generados?solicitudRadicadaId=…`
 *
 * Preview o descarga de un solo PDF: `generarPdfPorRadicacionYClave` / `generarPdfDirecto`.
 */

/**
 * Cuerpo del servicio **documentos-parametrizados** (paso 1 del flujo PDF de dos pasos).
 * Línea/grupo: mismos textos que en `TRL_P_CONTRATOS_FORMATOS` (código solo o etiqueta compuesta, según BD).
 * El mismo objeto se puede reutilizar dentro de `GenerarDocumentosSolicitudRequest` si llaman a orquestación.
 */
export interface SeleccionDocumentosRequest {
  idTipoSolicitud: number;
  idLineaNegocioOrigen?: string;
  idGrupoProgramaOrigen?: string;
  idLineaNegocioDestino?: string;
  idGrupoProgramaDestino?: string;
}

export interface DocumentoFormatoDTO {
  idPContratosFormatos: number;
  idTiposDocumentos: number;
  nombreDocumento: string;
  codigoDocumento: string;
  /** `null` si el formato aún no tiene plantilla Jasper en BD (sin preview por `/reports/...`). */
  nombreClaveJasper: string | null;
}

export interface ReportPdfParam {
  nombre: string;
  valor: string;
}

/**
 * Cuerpo de `POST /traslados/api/reports/{nombreClaveJasper}` o `POST /traslados/api/reports`.
 * Radicación: `idSolicitudesRadicadas` en raíz y/o `ID_RADICACION` dentro de `params`.
 */
export interface GenerarReportePdfRequest {
  idSolicitudesRadicadas?: number;
  params?: ReportPdfParam[];
  outputType?: string;
  outputFormat?: string;
  pdfPassword?: string | null;
  /** En POST sin segmento en URL (§3.2) es obligatorio; si también va en la URL debe coincidir. */
  nombreClaveJasper?: string;
}

/**
 * Body reutilizado en orquestación (`reporteRequest`); coincide con `ReportPdfRequestDTO` del motor.
 */
export interface ReportPdfRequestDTO {
  params: ReportPdfParam[];
  outputType: string;
  outputFormat: string;
  /** Cadena vacía si no aplica; alineado con MP-FT-1358 / motor Jasper. */
  pdfPassword?: string;
}

/**
 * `POST .../reports/generar-documentos-solicitud` — ruta **fija** del controlador; **no** es un
 * `nombreClaveJasper` (eso no existe en BD como fila: es solo el path HTTP).
 *
 * El backend, al recibir el body, **vuelve a consultar** la misma regla que
 * `POST /documentos-parametrizados` usando `seleccionDocumentos` y obtiene la lista de formatos;
 * por cada fila usa su `nombreClaveJasper` internamente hacia el motor Jasper. El front **no** envía
 * la clave Jasper en la raíz de este request.
 */
export interface GenerarDocumentosSolicitudRequest {
  idIntegrantesSolicitudRadicacion: number;
  /** Opcional en backend; se envía id de radicación o temporal para ruta en file server. */
  segmentoSolicitud?: string;
  /** Mismo criterio que `POST /documentos-parametrizados` (tipo + línea/grupo origen o destino). */
  seleccionDocumentos: SeleccionDocumentosRequest;
  reporteRequest?: ReportPdfRequestDTO | null;
}

export interface GeneracionDocumentoResultadoDTO {
  idPContratosFormatos: number;
  codigoDocumento: string;
  nombreDocumento: string;
  nombreClaveJasper: string;
  generado: boolean;
  rutaArchivo: string | null;
  tipoError?: 'TECNICO' | 'PARAMETRIZACION' | 'DATOS' | null;
  intentos?: number;
  mensaje: string;
}

export interface GeneracionDocumentosSolicitudResponseDTO {
  idIntegrantesSolicitudRadicacion: number;
  totalDocumentos: number;
  documentosGenerados: number;
  completado: boolean;
  resultados: GeneracionDocumentoResultadoDTO[];
}

export interface DocumentoGeneradoSolicitudDTO {
  idDocumentoGenerado: number;
  idSolicitudesRadicadas: number;
  idIntegrantesSolicitudRadicacion: number;
  nombreUsuarioIntegrante: string;
  idPContratosFormatos: number;
  codigoDocumento: string;
  idTiposDocumentos: number;
  tipoDocumento: string;
  nombreDocumento: string;
  nombreClaveJasper: string;
  rutaArchivo: string;
}

export interface DocumentoFaltanteFirmaDTO {
  idIntegrantesSolicitudRadicacion: number;
  idPContratosFormatos: number;
  /** Plantilla Jasper faltante (priorizar en mensajes al usuario). */
  nombreClaveJasper?: string;
  nombreDocumento?: string;
  motivo: string;
}

export interface ValidarFirmaSolicitudResponseDTO {
  idSolicitudRadicada: number;
  completaParaFirma: boolean;
  totalEsperados: number;
  totalGeneradosConRuta: number;
  faltantes: DocumentoFaltanteFirmaDTO[];
}

/** Resultado de preview: plantilla del paso 1 + PDF del paso 2. */
export interface DocumentoPdfPreviewResultado {
  documento: DocumentoFormatoDTO;
  pdf: Blob;
}

/** `POST …/solicitudes/documentos-generados/persistir-ruta` */
export interface PersistirRutaDocumentoGeneradoRequest {
  idIntegrantesSolicitudRadicacion: number;
  idPContratosFormatos: number;
  rutaArchivo: string;
}

/** PDF del motor + ruta en file server (cabecera/JSON del BFF o subida posterior). */
export interface GenerarReportePdfResultado {
  pdf: Blob;
  rutaArchivo: string;
}

@Injectable({
  providedIn: 'root',
})
export class GeneracionDocumentosTrasladosService {
  private documentosParametrizadosUrl = `${ENV.HOST}${URL_MAP.API.DOCUMENTOS_PARAMETRIZADOS}`;
  /** Orquestación MP-FT-1358 — no sustituye a `reports/{clave}` para un solo PDF. */
  private generarDocumentosSolicitudUrl = `${ENV.HOST}${URL_MAP.API.REPORTS_GENERAR_DOCUMENTOS_SOLICITUD}`;
  /**
   * Jasper del **BFF traslados** (`URL_MAP.API.REPORTS_GENERAR_DOCUMENTO_BASE` → `/traslados/api/reports`).
   * No usar `CERTIFICADOS` (`/ova/api/reports`, `ReportService`).
   */
  private reportsTrasladosBaseUrl = `${ENV.HOST}${URL_MAP.API.REPORTS_GENERAR_DOCUMENTO_BASE}`;
  private documentosGeneradosUrl = `${ENV.HOST}${URL_MAP.API.DOCUMENTOS_GENERADOS}`;
  private documentosGeneradosValidarFirmaUrl = `${ENV.HOST}${URL_MAP.API.DOCUMENTOS_GENERADOS_VALIDAR_FIRMA}`;
  private documentosGeneradosLoteUrl = `${ENV.HOST}${URL_MAP.API.DOCUMENTOS_GENERADOS_LOTE}`;
  private solicitudesDocumentosPersistirRutaUrl = `${ENV.HOST}${URL_MAP.API.SOLICITUDES_DOCUMENTOS_PERSISTIR_RUTA}`;

  constructor(private http: HttpClient) {}

  /**
   * **Paso 1** — `POST /traslados/api/documentos-parametrizados`: qué documentos aplican según
   * tipo de solicitud y línea/grupo (el `payload` lo arma el flujo real; no hay valores fijos aquí).
   * Respuesta: lista con `nombreClaveJasper` o `null` (ítem sin plantilla Jasper en BD → no se llama a `/reports/...`).
   */
  consultarDocumentosParametrizados(
    payload: SeleccionDocumentosRequest
  ): Observable<DocumentoFormatoDTO[]> {
    return this.http.post<DocumentoFormatoDTO[]>(
      this.documentosParametrizadosUrl,
      payload
    );
  }

  /** Mismo contrato que `consultarDocumentosParametrizados`; nombre orientado a negocio. */
  consultarDocumentosAGenerar(
    payload: SeleccionDocumentosRequest
  ): Observable<DocumentoFormatoDTO[]> {
    return this.consultarDocumentosParametrizados(payload);
  }

  /**
   * Body para **Paso 2** — `POST /traslados/api/reports/{nombreClaveJasper}`.
   * `idSolicitudesRadicadas` debe ser el id real devuelto al radicar (u otro origen válido del flujo).
   */
  buildReportePdfBodyPorRadicacion(
    idSolicitudesRadicadas: number
  ): GenerarReportePdfRequest {
    return {
      params: [
        { nombre: 'ID_RADICACION', valor: String(idSolicitudesRadicadas) },
      ],
      outputType: 'PDF',
      outputFormat: 'BYTES',
      pdfPassword: '',
    };
  }

  /**
   * **Paso 2** — un solo PDF: `POST .../reports/{nombreClaveJasper}`.
   * `nombreClaveJasper` debe ser el string devuelto en el paso 1 (p. ej. `negaciontraslado`); no se inventa en el servicio.
   */
  generarPdfPorRadicacionYClave(
    nombreClaveJasper: string,
    idSolicitudesRadicadas: number,
    bodyExtra?: Partial<GenerarReportePdfRequest>
  ): Observable<Blob> {
    return this.generarPdfPorRadicacionYClaveConDetalle(
      nombreClaveJasper,
      idSolicitudesRadicadas,
      bodyExtra,
    ).pipe(map((r) => r.pdf));
  }

  /**
   * Paso 2 con detalle: PDF + `rutaArchivo` si el BFF la devuelve (cabecera o JSON).
   */
  generarPdfPorRadicacionYClaveConDetalle(
    nombreClaveJasper: string,
    idSolicitudesRadicadas: number,
    bodyExtra?: Partial<GenerarReportePdfRequest>
  ): Observable<GenerarReportePdfResultado> {
    return this.generarPdfDirectoConDetalle(nombreClaveJasper, {
      ...this.buildReportePdfBodyPorRadicacion(idSolicitudesRadicadas),
      ...bodyExtra,
    });
  }

  /**
   * Flujo **preview** encadenado: (1) `documentos-parametrizados` con `seleccion` real →
   * (2) solo ítems con `nombreClaveJasper` distinto de `null`/vacío → `reports/{nombreClaveJasper}`
   * con body `params` + `PDF`/`BYTES`/`pdfPassword` como en contrato del motor.
   * Si un PDF falla, falla todo el `forkJoin`.
   */
  generarPdfsParametrizadosParaRadicacion(
    seleccion: SeleccionDocumentosRequest,
    idSolicitudesRadicadas: number
  ): Observable<DocumentoPdfPreviewResultado[]> {
    return this.consultarDocumentosParametrizados(seleccion).pipe(
      switchMap((docs) => {
        const conClave = (docs ?? []).filter(
          (d) => d?.nombreClaveJasper && String(d.nombreClaveJasper).trim()
        );
        if (conClave.length === 0) {
          return of([]);
        }
        return forkJoin(
          conClave.map((documento) =>
            this.generarPdfPorRadicacionYClave(
              String(documento.nombreClaveJasper).trim(),
              idSolicitudesRadicadas
            ).pipe(map((pdf) => ({ documento, pdf })))
          )
        );
      })
    );
  }

  /**
   * Orquestación MP-FT-1358: genera **todos** los documentos que correspondan a `seleccionDocumentos`,
   * sube PDFs y persiste rutas. La lista de plantillas **no** la arma el front: el backend ejecuta
   * internamente `listarDocumentosParametrizados(seleccionDocumentos)` (equivalente a llamar a
   * `documentos-parametrizados`). Para **un solo PDF** en navegador use `generarPdfDirecto` / `generarPdfPorRadicacionYClave`.
   */
  generarDocumentosSolicitud(
    payload: GenerarDocumentosSolicitudRequest
  ): Observable<GeneracionDocumentosSolicitudResponseDTO> {
    return this.http.post<GeneracionDocumentosSolicitudResponseDTO>(
      this.generarDocumentosSolicitudUrl,
      payload
    );
  }

  /**
   * **Paso 2** — Preview / descarga de **un** PDF: `POST .../reports/{nombreClaveJasper}` (doc §3.1).
   * El `nombreClaveJasper` debe venir del paso 1 (`consultarDocumentosParametrizados`).
   */
  generarPdfDirecto(
    nombreClaveJasper: string,
    payload: GenerarReportePdfRequest
  ): Observable<Blob> {
    return this.generarPdfDirectoConDetalle(nombreClaveJasper, payload).pipe(
      map((r) => r.pdf),
    );
  }

  generarPdfDirectoConDetalle(
    nombreClaveJasper: string,
    payload: GenerarReportePdfRequest
  ): Observable<GenerarReportePdfResultado> {
    const clave = String(nombreClaveJasper ?? '').trim();
    if (!clave) {
      throw new Error(
        'nombreClaveJasper es obligatorio en la URL para generar el PDF (catálogo documentos parametrizados).'
      );
    }
    return this.http
      .post(`${this.reportsTrasladosBaseUrl}/${encodeURIComponent(clave)}`, payload, {
        observe: 'response',
        responseType: 'blob',
      })
      .pipe(
        switchMap((response) =>
          from(this.mapRespuestaReportePdf(response)),
        ),
      );
  }

  /**
   * Carta de negación / carta de negación BG: `POST /reports` (sin segmento URL) con
   * `nombreClaveJasper: negacion_traslado`, `idSolicitudesRadicadas` y param `CEDULA`.
   */
  generarPdfCartaNegacionTrasladoConDetalle(
    idSolicitudesRadicadas: number,
    cedula: string,
  ): Observable<GenerarReportePdfResultado> {
    const idRadicacion = String(idSolicitudesRadicadas);
    const body: GenerarReportePdfRequest = {
      nombreClaveJasper: 'negacion_traslado',
      idSolicitudesRadicadas,
      params: [
        { nombre: 'ID_RADICACION', valor: idRadicacion },
        { nombre: 'CEDULA', valor: String(cedula ?? '').trim() },
      ],
      outputType: 'PDF',
      outputFormat: 'BYTES',
      pdfPassword: '',
    };
    return this.http
      .post(this.reportsTrasladosBaseUrl, body, {
        observe: 'response',
        responseType: 'blob',
      })
      .pipe(
        switchMap((response) =>
          from(this.mapRespuestaReportePdf(response)),
        ),
      );
  }

  /**
   * Registra en BD la ruta del PDF en file server (tras generar el documento).
   */
  persistirRutaDocumentoGenerado(
    payload: PersistirRutaDocumentoGeneradoRequest
  ): Observable<unknown> {
    return this.http.post(this.solicitudesDocumentosPersistirRutaUrl, payload);
  }

  private async mapRespuestaReportePdf(
    response: HttpResponse<Blob>,
  ): Promise<GenerarReportePdfResultado> {
    const pdf = response.body ?? new Blob();
    const rutaArchivo = await this.extraerRutaArchivoDesdeRespuestaReporte(
      response,
    );
    return { pdf, rutaArchivo };
  }

  private async extraerRutaArchivoDesdeRespuestaReporte(
    response: HttpResponse<Blob>,
  ): Promise<string> {
    const headerKeys = [
      'x-ruta-archivo',
      'ruta-archivo',
      'x-rutaarchivo',
      'rutaarchivo',
      'x-ruta',
    ];
    for (const key of headerKeys) {
      const v = String(response.headers.get(key) ?? '').trim();
      if (v) {
        return v;
      }
    }

    const contentType = String(response.headers.get('content-type') ?? '');
    if (contentType.includes('json') && response.body) {
      try {
        const text = (await response.body.text()).trim();
        if (text.startsWith('{')) {
          const o = JSON.parse(text) as Record<string, unknown>;
          return String(
            o['rutaArchivo'] ?? o['ruta'] ?? o['rutaArchivoGenerado'] ?? '',
          ).trim();
        }
      } catch {
        /* ignore */
      }
    }
    return '';
  }

  /**
   * Misma lógica que `generarPdfDirecto` pero con la clave **solo** en el body (doc §3.2).
   */
  generarPdfReporteSinSegmentoUrl(
    body: GenerarReportePdfRequest & { nombreClaveJasper: string }
  ): Observable<Blob> {
    const clave = String(body.nombreClaveJasper ?? '').trim();
    if (!clave) {
      throw new Error('nombreClaveJasper es obligatorio en el body para POST /reports.');
    }
    return this.http.post(`${this.reportsTrasladosBaseUrl}`, body, {
      responseType: 'blob',
    });
  }

  consultarDocumentosGenerados(
    solicitudRadicadaId: number
  ): Observable<DocumentoGeneradoSolicitudDTO[]> {
    return this.http.get<DocumentoGeneradoSolicitudDTO[]>(
      `${this.documentosGeneradosUrl}?solicitudRadicadaId=${solicitudRadicadaId}`
    );
  }

  validarSolicitudCompletaParaFirma(
    solicitudRadicadaId: number
  ): Observable<ValidarFirmaSolicitudResponseDTO> {
    return this.http.get<ValidarFirmaSolicitudResponseDTO>(
      `${this.documentosGeneradosValidarFirmaUrl}?solicitudRadicadaId=${solicitudRadicadaId}`
    );
  }

  registrarDocumentoGenerado(payload: {
    idIntegrantesSolicitudRadicacion: number;
    idPContratosFormatos: number;
  }): Observable<any> {
    return this.http.post(this.documentosGeneradosUrl, payload);
  }

  registrarDocumentosGeneradosLote(payload: {
    idIntegrantesSolicitudRadicacion: number;
    idsPContratosFormatos: number[];
  }): Observable<any> {
    return this.http.post(this.documentosGeneradosLoteUrl, payload);
  }
}
